import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getBlogBySlug, getBlogs } from '../../services/public/blogService';
import { 
    cleanHtmlContent, 
    formatBlogDate, 
    calculateReadTime,
    getAuthorInitials,
    getBlogGradient,
    getBlogIcon,
    getBlogTagColor
} from '../../utils/blogDataTransform';

const BlogDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    
    const [blog, setBlog] = useState(null);
    const [relatedBlogs, setRelatedBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const abortControllerRef = useRef(null);

    // Fetch blog details
    useEffect(() => {
        abortControllerRef.current = new AbortController();
        
        const fetchBlogDetails = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await getBlogBySlug(slug);
                
                if (response.success) {
                    setBlog(response.data);
                    setRelatedBlogs(response.relatedBlogs);                    
                } else {
                    setError('Failed to load blog post');
                }
            } catch (err) {
                if (err.code !== 'ERR_CANCELED' && err.name !== 'AbortError' && err.name !== 'CanceledError') {
                    console.error('Error fetching blog:', err);
                    setError('Failed to load blog post. Please try again.');
                }
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchBlogDetails();
        }

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [slug]);

    // Handle back button
    const handleBack = (e) => {
        e.preventDefault();
        navigate('/blog');
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen pt-24 pb-16 container mx-auto px-4">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0096C7] mx-auto mb-4"></div>
                    <p className="text-gray-400">Loading article...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !blog) {
        return (
            <div className="min-h-screen pt-24 pb-16 container mx-auto px-4">
                {/* SEO Settings */}
                <title>{`Blog Detail | ${process.env.REACT_APP_NAME} Blog`}</title>

                <div className="text-center">
                    <i className="fas fa-exclamation-triangle text-red-500 text-4xl mb-4"></i>
                    <h3 className="text-xl font-bold text-white mb-2">Something went wrong</h3>
                    <p className="text-gray-400 mb-6">{error || 'Blog post not found'}</p>
                    <button 
                        onClick={() => navigate('/blog')}
                        className="bg-gradient-to-r from-[#0096C7] to-[#4361EE] text-white px-6 py-3 rounded-xl hover:shadow-lg hover:shadow-[#0096C7]/20 transition-all duration-300"
                    >
                        Back to Blog
                    </button>
                </div>
            </div>
        );
    }

    const readTime = calculateReadTime(blog.fullDescription);
    const formattedDate = formatBlogDate(blog.createdAt);
    const authorInitials = getAuthorInitials(blog.createdBy?.name);
    const gradient = getBlogGradient(blog.tags?.[0]);
    const icon = getBlogIcon(blog.tags?.[0]);
    const tagColor = getBlogTagColor(blog.tags?.[0]);

    return (
        <div className="container mx-auto px-4 md:px-6 pt-24 pb-16">
            {/* SEO Settings */}
            <title>{`${blog.metaTags.title || blog.title} | ${process.env.REACT_APP_NAME} Blog`}</title>
            <meta
                name="description"
                content={blog.metaTags.description || blog.description}
            />

            {/* Back to Blog Link */}
            <div className="mb-8">
                <button 
                    onClick={handleBack}
                    className="inline-flex items-center text-[#0096C7] hover:text-[#00a8e0] font-medium bg-transparent border-none cursor-pointer"
                >
                    <i className="fas fa-arrow-left mr-2" /> Back to All Articles
                </button>
            </div>
            
            {/* Article Header */}
            <div className="mb-12">
                <h1 className="text-lg md:text-2xl lg:text-3xl font-bold mb-6">
                    {blog.title}
                </h1>
                <p className="text-md text-gray-300 mb-8">
                    {blog.description}
                </p>
                
                <div className="flex flex-wrap items-center gap-4 mb-6">
                    {blog.tags && blog.tags.length > 0 && (
                        <span 
                            className="px-3 py-1 bg-gray-800 text-xs rounded-full font-medium"
                            style={{ color: tagColor }}
                        >
                            {blog.tags[0]}
                        </span>
                    )}
                    
                    <div className="flex items-center space-x-4">
                        <span className="text-gray-400 text-sm">
                            <i className="far fa-clock mr-1" /> {readTime}
                        </span>
                        {/* <span className="text-gray-400 text-sm">
                            <i className="far fa-calendar mr-1" /> {formattedDate}
                        </span> */}
                    </div>
                </div>

                {/* Author Info */}
                {/* <div className="flex items-center pt-4 border-t border-gray-800">
                    <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center mr-3">
                        <span className="text-sm font-bold text-gray-300">
                            {authorInitials}
                        </span>
                    </div>
                    <div>
                        <p className="text-white font-medium">
                            {blog.createdBy?.name || 'Hunar Pool'}
                        </p>
                        <p className="text-gray-400 text-xs">Author</p>
                    </div>
                </div> */}
            </div>

            {/* Article Image */}
            <div className="relative h-64 md:h-80 lg:h-96 rounded-2xl overflow-hidden mb-12">
                {blog.featuredImage ? (
                    <img 
                        src={blog.featuredImage} 
                        alt={blog.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.parentNode.innerHTML = `
                                <div class="w-full h-full bg-gradient-to-br ${gradient} flex items-center justify-center">
                                    <i class="fas ${icon} text-8xl opacity-70" style="color: ${tagColor}"></i>
                                </div>
                            `;
                        }}
                    />
                ) : (
                    <div className={`w-full h-full bg-gradient-to-br ${gradient} flex items-center justify-center`}>
                        <i className={`fas ${icon} text-8xl opacity-70`} style={{ color: tagColor }}></i>
                    </div>
                )}
            </div>
            
            {/* Article Content & Sidebar */}
            <div className="flex flex-col lg:flex-row gap-12">
                {/* Main Article Content */}
                <div className="lg:w-2/3">
                    <div 
                        className="blog-content prose prose-invert max-w-none"
                        dangerouslySetInnerHTML={{ 
                            __html: cleanHtmlContent(blog.fullDescription || '') 
                        }}
                    />

                    {/* Meta Tags/Keywords */}
                    {blog.metaTags?.keywords && blog.metaTags.keywords.length > 0 && (
                        <div className="mt-8 pt-8 border-t border-gray-800">
                            <h3 className="text-lg font-bold text-white mb-4">Topics</h3>
                            <div className="flex flex-wrap gap-2">
                                {blog.metaTags.keywords.map((keyword, index) => (
                                    <span 
                                        key={index}
                                        className="px-3 py-1.5 bg-gray-800 text-gray-300 rounded-lg text-sm"
                                    >
                                        #{keyword}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div className="lg:w-1/3">
                    {/* Related Articles */}
                    {relatedBlogs.length > 0 && (
                        <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800 mb-8 sticky top-24">
                            <h3 className="text-xl font-bold mb-6">Related Articles</h3>
                            <div className="space-y-6">
                                {relatedBlogs.map((relatedBlog, index) => {
                                    const relatedReadTime = calculateReadTime(relatedBlog.fullDescription);
                                    const relatedGradient = getBlogGradient(relatedBlog.tags?.[0], index);
                                    const relatedIcon = getBlogIcon(relatedBlog.tags?.[0]);
                                    const relatedColor = getBlogTagColor(relatedBlog.tags?.[0]);
                                    
                                    return (
                                        <Link 
                                            key={relatedBlog._id}
                                            to={`/blog/${relatedBlog.slug}`}
                                            className="flex items-start group"
                                        >
                                            <div className={`w-16 h-16 rounded-lg bg-gradient-to-br ${relatedGradient} flex items-center justify-center mr-4 flex-shrink-0`}>
                                                <i className={`fas ${relatedIcon}`} style={{ color: relatedColor }}></i>
                                            </div>
                                            <div>
                                                <h4 className="font-bold mb-1 group-hover:text-[#0096C7] transition line-clamp-2">
                                                    {relatedBlog.title}
                                                </h4>
                                                <p className="text-gray-400 text-sm">{relatedReadTime}</p>
                                            </div>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BlogDetail;